import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '@/modules/auth';
import { useToast } from '@/hooks/useToast';
import DarkPageContainer from '@/shared/components/DarkPageContainer';
import { Card } from '@/shared/components/Card';
import { PrimaryButton, SecondaryButton, TertiaryButton, DangerButton } from '@/shared/components/Button';
import { DarkPageHeader, DarkSectionHeader, DarkAlert, DarkBadge, DarkInput, DarkTextarea } from '@/shared/components/DarkFormElements';
import ConfirmModal from '@/shared/components/ConfirmModal';
import AppChargesSection from '@/modules/user/components/AppChargesSection';
import ConversationTimeline from '@/modules/user/components/ConversationTimeline';
import RequestFeatureModal from '@/modules/user/components/RequestFeatureModal';
import { getCurrencySymbol } from '../../../shared/constants/billing.js';
import { ADMIN_EMAILS } from '../../../shared/config/admins.js';
import { useApp, useUpdateApp, useApproveApp, useAddNote, queryKeys } from '@/hooks/queries';
import { captureError } from '@/utils/errorHandler';

export default function AppDetailPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const actualAppId = appId;
  const queryClient = useQueryClient();
  const { authFetch, user } = useAuthContext();
  const { showToast, toasts, hideToast } = useToast();
  
  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email);
  
  // Query hooks
  const { data: app, isLoading: appLoading, error: appError, refetch: refetchApp } = useApp(actualAppId);
  
  // Mutation hooks
  const updateAppMutation = useUpdateApp();
  const approveAppMutation = useApproveApp();
  const addNoteMutation = useAddNote();
  
  const loading = appLoading;
  const error = appError?.message || null;
  const repositories = app?.repositories || [];
  
  // Edit mode states
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    appUrl: '',
    tags: [],
    status: 'draft',
  });
  const [newTagInput, setNewTagInput] = useState('');
  
  // Notes states
  const [newNoteContent, setNewNoteContent] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  const [submitting, setSubmitting] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approving, setApproving] = useState(false);
  const [showFeatureModal, setShowFeatureModal] = useState(false);
  const [submittingFeature, setSubmittingFeature] = useState(false);

  // Sync edit form with app data when it loads/changes
  useEffect(() => {
    if (app) {
      setEditForm({
        name: app.name,
        description: app.description || '',
        appUrl: app.appUrl,
        tags: app.tags || [],
        status: app.status || 'draft',
      });
    }
  }, [app]);

  // Handle app load error
  useEffect(() => {
    if (appError) {
      navigate('/dashboard');
    }
  }, [appError, navigate]);

  async function handleSaveEdit() {
    try {
      setSubmitting(true);
      const updateData = {
        id: actualAppId,
        ...editForm,
      };
      delete updateData.appUrl;
      
      await updateAppMutation.mutateAsync(updateData);
      setIsEditing(false);
      setSubmitting(false);
    } catch (err) {
      captureError(err, {
        action: 'handleSaveEdit',
        component: 'AppDetailPage',
        extra: { appId: actualAppId, updateData },
      });
      console.error('Error updating app:', err);
      showToast('Failed to update app: ' + err.message, 'error');
      setSubmitting(false);
    }
  }

  async function handleRemoveRepository(repoId) {
    if (!confirm('Remove this repository?')) return;
    
    try {
      const response = await authFetch(`/api/apps/remove-repository?repositoryId=${repoId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to remove repository');
      }

      refetchApp();
    } catch (err) {
      captureError(err, {
        action: 'handleRemoveRepository',
        component: 'AppDetailPage',
        extra: { appId: actualAppId, repoId },
      });
      console.error('Error removing repository:', err);
      showToast('Failed to remove repository: ' + err.message, 'error');
    }
  }

  if (loading) {
    return (
      <DarkPageContainer 
        loading={true} 
        loadingText="Loading app details..."
        toasts={toasts}
        onCloseToast={hideToast}
        maxWidth="6xl"
      />
    );
  }

  if (error || !app) {
    return (
      <DarkPageContainer toasts={toasts} onCloseToast={hideToast} maxWidth="6xl">
        <Card variant="dark" className="text-center py-12">
          <p className="text-red-400 mb-4">{error || 'App not found'}</p>
          <TertiaryButton dark onClick={() => navigate('/dashboard')}>
            ← Back to Dashboard
          </TertiaryButton>
        </Card>
      </DarkPageContainer>
    );
  }

  const appNotes = app?.notes || [];
  const isPendingApproval = app?.approvalStatus === 'pending_approval';
  const isApproved = app?.approvalStatus === 'approved';
  const isQuoteRequested = app?.selectedPaymentMethod === 'quote' && !app.quotedAt;

  const handleApproveConfirm = async () => {
    setShowApproveModal(false);
    setApproving(true);
    try {
      await approveAppMutation.mutateAsync(actualAppId);
      showToast('✅ Changes approved! We will complete production setup and make it live.', 'success');
    } catch (error) {
      captureError(error, {
        action: 'handleApproveConfirm',
        component: 'AppDetailPage',
        extra: { appId: actualAppId },
      });
      console.error('Error approving changes:', error);
      showToast(error.message || 'Failed to approve changes', 'error');
    } finally {
      setApproving(false);
    }
  };

  const handleArchiveConfirm = async () => {
    try {
      setSubmitting(true);
      setShowArchiveModal(false);
      await updateAppMutation.mutateAsync({ id: actualAppId, status: 'archived' });
      setEditForm({ ...editForm, status: 'archived' });
      showToast('App archived', 'success');
    } catch (err) {
      captureError(err, {
        action: 'handleArchiveConfirm',
        component: 'AppDetailPage',
        extra: { appId: actualAppId },
      });
      console.error('Error archiving app:', err);
      showToast('Failed to archive app: ' + err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DarkPageContainer toasts={toasts} onCloseToast={hideToast} maxWidth="6xl">
      <ConfirmModal
        isOpen={showArchiveModal}
        onClose={() => setShowArchiveModal(false)}
        onConfirm={handleArchiveConfirm}
        title="Archive App"
        message="Archive this app? It will be hidden from your dashboard."
        confirmText="Archive"
        cancelText="Cancel"
        confirmButtonColor="bg-red-600 hover:bg-red-700"
        isLoading={submitting}
      />
      <ConfirmModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={handleApproveConfirm}
        title="Approve Changes"
        message="Approve these changes? We will move them to production after completing setup."
        confirmText="Approve"
        cancelText="Cancel"
        confirmButtonColor="bg-green-600 hover:bg-green-700"
        isLoading={approving}
      />

      {/* Header */}
      <DarkPageHeader
        title={app.name}
        backLink="/dashboard"
        backText="← Back to Dashboard"
        actions={
          <div className="flex flex-wrap gap-3">
            {app.status === 'live' && app.appUrl && (
              <PrimaryButton
                onClick={() => window.open(app.appUrl, '_blank')}
                size="md"
              >
                Launch App →
              </PrimaryButton>
            )}
            {(app.status === 'live' || app.status === 'draft') && (
              <TertiaryButton
                dark
                onClick={() => setShowArchiveModal(true)}
                disabled={submitting}
                size="md"
              >
                Archive
              </TertiaryButton>
            )}
            {app.status === 'archived' && (
              <SecondaryButton
                onClick={async () => {
                  try {
                    setSubmitting(true);
                    const targetStatus = app.appUrl ? 'live' : 'draft';
                    const updated = await updateAppMutation.mutateAsync({ id: actualAppId, status: targetStatus });
                    setEditForm({ ...editForm, status: updated.status });
                    showToast(`App reactivated to ${targetStatus}`, 'success');
                  } catch (err) {
                    captureError(err, {
                      action: 'handleReactivateApp',
                      component: 'AppDetailPage',
                      extra: { appId: actualAppId, targetStatus },
                    });
                    console.error('Error reactivating app:', err);
                    showToast('Failed to reactivate app: ' + err.message, 'error');
                  } finally {
                    setSubmitting(false);
                  }
                }}
                disabled={submitting}
                size="md"
              >
                Reactivate
              </SecondaryButton>
            )}
          </div>
        }
      />

      {/* Status Badge */}
      <div className="mb-6">
        <StatusBadge status={app.status} />
      </div>

      {/* Quote Requested Banner */}
      {isQuoteRequested && (
        <Card variant="dark-bordered" className="mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            <div className="text-5xl">📧</div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-2xl font-bold text-white mb-2">Quote Requested</h2>
              <p className="text-gray-300">
                We are reviewing your app and will send you a quote soon.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Building Status Banner */}
      {app.status === 'draft' && !isPendingApproval && !isApproved && (
        <Card variant="dark-highlighted" className="mb-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-4">
            <div className="text-4xl">🚀</div>
            <div className="flex-1 text-center md:text-left">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">Your App is Being Built</h2>
              <p className="text-gray-300">
                We're working on your app. Check the updates below for progress, and feel free to send us a message or request new features anytime.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Approval Banner */}
      {isPendingApproval && (
        <DarkAlert variant="warning" icon="⏳" title="Review & Approve Changes" className="mb-6">
          <p className="mb-4">
            We've completed some work on <strong>{app.name}</strong> and would like you to review the changes before we move them to production.
          </p>
          
          {app.devUrl && (
            <div className="bg-white/10 rounded-lg p-4 mb-4">
              <p className="text-sm font-medium text-gray-300 mb-2">Preview URL:</p>
              <a
                href={app.devUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zapt-yellow hover:text-zapt-yellow/80 font-medium text-lg break-all"
              >
                {app.devUrl} →
              </a>
            </div>
          )}
          
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowApproveModal(true)}
              disabled={approving}
              className="px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-400 font-semibold disabled:opacity-50 transition-colors"
            >
              {approving ? 'Approving...' : '✅ Approve Changes'}
            </button>
          </div>
          
          <p className="text-sm mt-4 opacity-75">
            If you need adjustments, send us a message or request a feature below.
          </p>
        </DarkAlert>
      )}

      {isApproved && (
        <DarkAlert variant="success" icon="✅" title="Changes Approved" className="mb-6">
          Your changes have been approved. We will complete the production setup and make it live soon.
        </DarkAlert>
      )}

      {/* App Information Card */}
      <Card variant="dark" className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">App Information</h2>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 text-zapt-yellow hover:text-zapt-yellow/80 font-medium transition-colors"
            >
              Edit
            </button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-4">
            <DarkInput
              label="Name"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
            />
            
            <DarkTextarea
              label="Description"
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              rows={10}
              resizable
            />

            {editForm.appUrl && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">URL</label>
                <input
                  type="url"
                  value={editForm.appUrl}
                  readOnly
                  disabled
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-gray-400 cursor-not-allowed"
                />
                <p className="mt-1 text-xs text-gray-500">URL cannot be changed</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
              <div className="flex flex-wrap gap-2 mb-3">
                {editForm.tags && editForm.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-zapt-purple/30 text-purple-200 rounded-full text-sm border border-zapt-purple/50"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => {
                        const newTags = editForm.tags.filter((_, i) => i !== index);
                        setEditForm({ ...editForm, tags: newTags });
                      }}
                      className="ml-1 text-purple-300 hover:text-white"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <DarkInput
                  value={newTagInput}
                  onChange={(e) => setNewTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const trimmed = newTagInput.trim();
                      if (trimmed && !editForm.tags.includes(trimmed)) {
                        setEditForm({ ...editForm, tags: [...editForm.tags, trimmed] });
                        setNewTagInput('');
                      }
                    }
                  }}
                  placeholder="Add a tag and press Enter"
                  className="flex-1"
                />
                <SecondaryButton
                  onClick={() => {
                    const trimmed = newTagInput.trim();
                    if (trimmed && !editForm.tags.includes(trimmed)) {
                      setEditForm({ ...editForm, tags: [...editForm.tags, trimmed] });
                      setNewTagInput('');
                    }
                  }}
                  size="md"
                >
                  Add
                </SecondaryButton>
              </div>
            </div>

            {app.status === 'draft' && (
              <DarkAlert variant="info" icon="🚧">
                This app is currently being built. Status changes are managed by the admin team.
              </DarkAlert>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <PrimaryButton
                onClick={handleSaveEdit}
                disabled={submitting}
                isLoading={submitting}
                size="md"
              >
                Save Changes
              </PrimaryButton>
              <TertiaryButton
                dark
                onClick={() => {
                  setIsEditing(false);
                  setEditForm({
                    name: app.name,
                    description: app.description || '',
                    appUrl: app.appUrl,
                    tags: app.tags || [],
                    status: app.status || 'draft',
                  });
                  setNewTagInput('');
                }}
                disabled={submitting}
                size="md"
              >
                Cancel
              </TertiaryButton>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {app.description && (
              <div>
                <p className="text-sm font-medium text-gray-400 mb-1">Description</p>
                <p className="text-gray-200 whitespace-pre-wrap">{app.description}</p>
              </div>
            )}

            {app.details && (
              <div className="pt-4 border-t border-white/10">
                <p className="text-sm font-medium text-gray-400 mb-2">Features & Details</p>
                <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                  <pre className="text-sm text-gray-200 whitespace-pre-wrap font-sans">{app.details}</pre>
                </div>
              </div>
            )}
            
            {app.appUrl && (
              <div>
                <p className="text-sm font-medium text-gray-400 mb-1">Live URL</p>
                <a
                  href={app.appUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zapt-yellow hover:text-zapt-yellow/80"
                >
                  {app.appUrl}
                </a>
                {app.liveSince && (
                  <p className="text-xs text-gray-500 mt-1">
                    Live since {new Date(app.liveSince).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                )}
              </div>
            )}

            {app.devUrl && (
              <div>
                <p className="text-sm font-medium text-gray-400 mb-1">Preview URL</p>
                <a
                  href={app.devUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zapt-yellow hover:text-zapt-yellow/80"
                >
                  {app.devUrl}
                </a>
                <p className="text-xs text-gray-500 mt-1">Development/preview environment for reviewing changes</p>
              </div>
            )}

            {app.tags && app.tags.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-400 mb-2">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {app.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 bg-zapt-purple/30 text-purple-200 rounded-full text-sm border border-zapt-purple/50"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-3 border-t border-white/10">
              <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                {app.createdAt && (
                  <div>
                    <span className="font-medium">Created:</span>{' '}
                    {new Date(app.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                )}
                {app.updatedAt && (
                  <div>
                    <span className="font-medium">Updated:</span>{' '}
                    {new Date(app.updatedAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Payment & Billing Section */}
      <Card variant="dark" className="mb-6">
        <h2 className="text-xl font-bold text-white mb-4">💳 Payment & Billing</h2>
        
        {/* Payment Method */}
        {app?.selectedPaymentMethod ? (
          <div className="mb-4 pb-4 border-b border-white/10">
            <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
              <span className="text-gray-300 font-medium">Payment Method:</span>
              <span className="font-semibold text-white">
                💳 Quote
              </span>
            </div>
            {app.preferredCurrency && (
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg mt-2">
                <span className="text-gray-400 text-sm">Preferred Currency:</span>
                <span className="font-medium text-white uppercase">{app.preferredCurrency}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="mb-4 pb-4 border-b border-white/10">
            <div className="p-3 bg-white/5 rounded-lg">
              <span className="text-gray-400 text-sm">No payment method selected</span>
            </div>
          </div>
        )}

        {/* Quote Payment Details */}
        {app?.selectedPaymentMethod === 'quote' && app?.quotedAt && (
          <div className="space-y-3 mb-4">
            <h3 className="text-sm font-semibold text-gray-300 mb-2">Quote Details</h3>
            {app.quotedAmount && (
              <div className="flex items-center justify-between p-3 bg-zapt-purple/20 rounded-lg border border-zapt-purple/40">
                <span className="text-gray-300">Total Quote:</span>
                <span className="font-semibold text-white">
                  {getCurrencySymbol(app.preferredCurrency || 'usd')}{parseFloat(app.quotedAmount || 0).toFixed(2)}
                </span>
              </div>
            )}
            {app.depositAmount != null && parseFloat(app.depositAmount) > 0 && (
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-gray-300">
                  Deposit{app.quotedAmount && parseFloat(app.quotedAmount) > 0
                    ? ` (${Math.round((parseFloat(app.depositAmount) / parseFloat(app.quotedAmount)) * 100)}%)`
                    : ''}:
                </span>
                <span className={`font-semibold ${app.depositPaidAt ? 'text-green-400' : 'text-white'}`}>
                  {getCurrencySymbol(app.preferredCurrency || 'usd')}{parseFloat(app.depositAmount || 0).toFixed(2)} {app.depositPaidAt && '✓ Paid'}
                </span>
              </div>
            )}
            {app.finalAmount != null && parseFloat(app.finalAmount) > 0 && (
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-gray-300">
                  Final Payment{app.quotedAmount && parseFloat(app.quotedAmount) > 0
                    ? ` (${Math.round((parseFloat(app.finalAmount) / parseFloat(app.quotedAmount)) * 100)}%)`
                    : ''}:
                </span>
                <span className={`font-semibold ${app.finalPaidAt ? 'text-green-400' : 'text-white'}`}>
                  {getCurrencySymbol(app.preferredCurrency || 'usd')}{parseFloat(app.finalAmount || 0).toFixed(2)} {app.finalPaidAt && '✓ Paid'}
                </span>
              </div>
            )}
            {app.quotedAt && !app.depositPaidAt && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <PrimaryButton
                  onClick={() => navigate(`/app/${actualAppId}/quote`)}
                  className="w-full"
                  size="lg"
                >
                  Review & Pay Deposit ({getCurrencySymbol(app.preferredCurrency || 'usd')}{parseFloat(app.depositAmount || 0).toFixed(2)})
                </PrimaryButton>
              </div>
            )}
            {app.depositPaidAt && !app.finalPaidAt && app.approvalStatus === 'approved' && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <PrimaryButton
                  onClick={async () => {
                    try {
                      setSubmitting(true);
                      const response = await authFetch('/api/apps/pay-final', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ appId: actualAppId }),
                      });

                      if (!response.ok) {
                        const error = await response.json();
                        throw new Error(error.error || 'Failed to create payment session');
                      }

                      const data = await response.json();
                      if (data.checkoutUrl) {
                        window.location.href = data.checkoutUrl;
                      }
                    } catch (error) {
                      captureError(error, {
                        action: 'handlePayFinalPayment',
                        component: 'AppDetailPage',
                        extra: { appId: actualAppId },
                      });
                      console.error('Error paying final payment:', error);
                      showToast(error.message || 'Failed to pay final payment', 'error');
                      setSubmitting(false);
                    }
                  }}
                  disabled={submitting}
                  isLoading={submitting}
                  className="w-full"
                  size="lg"
                >
                  Pay Final Payment ({getCurrencySymbol(app.preferredCurrency || 'usd')}{parseFloat(app.finalAmount || 0).toFixed(2)})
                </PrimaryButton>
              </div>
            )}
          </div>
        )}

        {/* Monthly Maintenance Fee */}
        <div className="pt-4 border-t border-white/10">
          <h3 className="text-sm font-semibold text-gray-300 mb-2">Monthly Maintenance Fee</h3>
          {app?.monthlyMaintenanceFee && parseFloat(app.monthlyMaintenanceFee) > 0 ? (
            <>
              <div className="flex items-center justify-between p-3 bg-zapt-yellow/10 rounded-lg border border-zapt-yellow/30">
                <div>
                  <span className="text-white font-medium">{getCurrencySymbol(app.preferredCurrency || 'usd')}{parseFloat(app.monthlyMaintenanceFee).toFixed(2)}/month</span>
                  <p className="text-xs text-gray-400 mt-1">
                    {app.status === 'live' ? (
                      <>
                        <span className="text-green-400 font-medium">• Active</span>
                        <span className="ml-1">Charged monthly via Stripe</span>
                      </>
                    ) : (
                      <span>Will be charged when app goes live</span>
                    )}
                  </p>
                </div>
              </div>
              
              {app.status === 'live' && (
                <div className="mt-3">
                  {app.hasMonthlyPaymentMethod ? (
                    <DarkAlert variant="success" icon="✅">
                      Payment method configured. Subscription billing will be charged automatically.
                    </DarkAlert>
                  ) : (
                    <DarkAlert variant="warning" icon="💳" title="Payment Method Required">
                      <p className="mb-3">
                        To enable automatic monthly billing, please set up a payment method.
                      </p>
                      <button
                        onClick={async () => {
                          try {
                            const returnUrl = `${window.location.origin}/app/${actualAppId}`;
                            const response = await authFetch(`/api/users/customer-portal?returnUrl=${encodeURIComponent(returnUrl)}`);
                            
                            if (!response.ok) {
                              const error = await response.json();
                              throw new Error(error.error || 'Failed to open billing portal');
                            }
                            
                            const data = await response.json();
                            window.location.href = data.portalUrl;
                          } catch (error) {
                            captureError(error, {
                              action: 'handleOpenBillingPortal',
                              component: 'AppDetailPage',
                              extra: { appId: actualAppId },
                            });
                            showToast(error.message || 'Failed to open billing portal', 'error');
                          }
                        }}
                        className="px-4 py-2 bg-yellow-500 text-yellow-900 rounded-lg hover:bg-yellow-400 font-semibold text-sm transition-colors"
                      >
                        Set Up Payment Method →
                      </button>
                    </DarkAlert>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <span className="text-gray-400 text-sm">{getCurrencySymbol(app.preferredCurrency || 'usd')}0.00/month - No monthly maintenance fee</span>
            </div>
          )}
        </div>

        {/* View Charges Link */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <button
            onClick={() => {
              const chargesSection = document.getElementById('app-charges-section');
              if (chargesSection) {
                chargesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }}
            className="text-sm text-zapt-yellow hover:text-zapt-yellow/80 font-medium underline"
          >
            View Charges →
          </button>
        </div>
      </Card>

      {/* Charges Section */}
      <div id="app-charges-section" className="mb-6">
        <AppChargesSection
          appId={actualAppId}
          showToast={showToast}
          dark={true}
        />
      </div>

      {/* Conversation Timeline */}
      <div className="mb-6">
        <ConversationTimeline
          notes={appNotes}
          currentUserEmail={user?.email}
          addingNote={addingNote}
          dark={true}
          onSendMessage={async (content) => {
            setAddingNote(true);
            try {
              await addNoteMutation.mutateAsync({
                appId: actualAppId,
                content: content,
                source: 'customer',
                noteType: 'message',
              });
              showToast('✅ Message sent!', 'success');
            } catch (error) {
              captureError(error, {
                action: 'handleSendMessage',
                component: 'AppDetailPage',
                extra: { appId: actualAppId },
              });
              showToast(error.message || 'Failed to send message', 'error');
            } finally {
              setAddingNote(false);
            }
          }}
          onRequestFeature={() => setShowFeatureModal(true)}
        />
      </div>

      {/* Request Feature Modal */}
      <RequestFeatureModal
        isOpen={showFeatureModal}
        onClose={() => setShowFeatureModal(false)}
        isSubmitting={submittingFeature}
        appDescription={app?.description || ''}
        existingFeatures={app?.tags || []}
        onSubmit={async (features) => {
          setSubmittingFeature(true);
          try {
            // 1. Create a single feature_request note combining all features
            const featuresList = features
              .map(f => f.description 
                ? `• **${f.name}**: ${f.description}`
                : `• **${f.name}**`
              )
              .join('\n');
            
            // For single feature, show name + description
            // For multiple features, just list them (the note type badge already shows "Feature Request")
            const content = features.length === 1
              ? (features[0].description 
                  ? `**${features[0].name}**\n\n${features[0].description}`
                  : `**${features[0].name}**`)
              : featuresList;
            
            // Add the note using mutation
            await addNoteMutation.mutateAsync({
              appId: actualAppId,
              content: content,
              source: 'customer',
              noteType: 'feature_request',
            });
            
            // 2. Update the app description with the new features
            const currentDescription = app?.description || '';
            const requestedFeaturesSection = '\n\n**Requested Features:**\n' + 
              features.map(f => `- ${f.name}${f.description ? `: ${f.description}` : ''}`).join('\n');
            
            // Check if there's already a "Requested Features" section
            let updatedDescription;
            if (currentDescription.includes('**Requested Features:**')) {
              // Append to existing section
              updatedDescription = currentDescription + '\n' + 
                features.map(f => `- ${f.name}${f.description ? `: ${f.description}` : ''}`).join('\n');
            } else {
              // Add new section
              updatedDescription = currentDescription + requestedFeaturesSection;
            }
            
            // Update app description using mutation
            await updateAppMutation.mutateAsync({
              id: actualAppId,
              description: updatedDescription,
            });
            
            setShowFeatureModal(false);
            showToast(`✅ ${features.length} feature${features.length !== 1 ? 's' : ''} requested!`, 'success');
          } catch (error) {
            captureError(error, {
              action: 'handleSubmitFeatureRequest',
              component: 'AppDetailPage',
              extra: { appId: actualAppId, featuresCount: features.length },
            });
            showToast(error.message || 'Failed to submit feature request', 'error');
          } finally {
            setSubmittingFeature(false);
          }
        }}
      />
    </DarkPageContainer>
  );
}

function StatusBadge({ status }) {
  const statusConfig = {
    draft: { label: 'Draft', variant: 'default' },
    live: { label: 'Live', variant: 'success' },
    archived: { label: 'Archived', variant: 'error' },
  };

  const config = statusConfig[status] || { label: status, variant: 'default' };

  return (
    <DarkBadge variant={config.variant}>
      {config.label}
    </DarkBadge>
  );
}
